import "./SupportTicket.css";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  MoveHorizontal,
  AlertCircle,
  ArrowUpFromDot,
  CalendarDays,
  Check,
  Circle,
  Clock3,
  FileText,
  List,
  Loader,
  Megaphone,
  MessageCircle,
  PauseCircle,
  Smartphone,
  ArrowDown,
  Ticket,
  ToggleRight,
  UserPen,
  XCircle,
  Flame,
  ShieldAlert,
  Button,
  Input,
} from "../../ui";

import {
  getMySupportsAPI,
  getSupportByIdAPI,
  addSupportMessageAPI,
  updateSupportStatusAPI,
  closeSupportAPI,
  updateSupportPriorityAPI,
  addInternalNoteAPI,
  getCategoriesAPI,
  deleteSupportAPI,
} from "../../../utils/api/support";

import {
  normalizeCategories,
  normalizeTickets,
} from "../supportPage/helpers/supportHelpers";
import BulkAssignBar from "./assignBulkTicket/AssignBulkTicket";
import FilterSidebar from "./supportSide/SupportSide";
import TicketDetails from "./ticketDetails/TicketDetails";
import TicketList from "./ticketList/TicketList";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { ConfirmModal } from "../../modals/Confirm";

const TEXT = {
  ar: {
    total: "إجمالي التذاكر",
    openCount: "مفتوحة",
    inProgressCount: "جاري المعالجة",
    allTickets: "جميع التذاكر",
    search: "بحث...",
    searchLabel: "بحث في التذاكر",
    empty: "لا توجد تذاكر",
    loading: "جاري تحميل التذاكر...",
    loadFailed: "تعذر تحميل التذاكر.",
    retry: "إعادة المحاولة",
    actionFailed: "تعذر تنفيذ العملية. حاول مرة أخرى.",
    notAllowed: "ليست لديك صلاحية لتنفيذ هذا الإجراء على تذكرة هذا المطعم.",
    bulkAssign: "تعيين جماعي",
    deleteTicket: "حذف التذكرة",
    deleteConfirm: "سيتم حذف هذه التذكرة نهائياً.",
    byType: "حسب النوع",
    byStatus: "حسب الحالة",
    byPriority: "حسب الاهمية",
    all: "الكل",
    open: "مفتوح",
    assigned: "معين",
    in_progress: "جاري المعالجة",
    resolved: "تم الحل",
    closed: "مغلق",
    app_issue: "مشكلة تطبيق",
    menu_update: "تعديل القائمة",
    working_hours: "أوقات العمل",
    invoice_request: "طلب فاتورة",
    rider_issue: "مشكلة مندوب",
    app_status: "تغيير حالة تطبيق",
    meeting_request: "طلب اجتماع",
    offer_request: "طلب حملة",
    registration_request: "اشتراك تطبيق جديد",
    update_data: "تحديث البيانات",
    other: "استفسار آخر",
    details: "تفاصيل التذكرة",
    category: "نوع الطلب",
    priority: "الأولوية",
    app: "التطبيق",
    orderNumber: "رقم الطلب",
    description: "الوصف",
    timeline: "سجل التذكرة",
    messages: "الرسائل",
    replyPlaceholder: "اكتب ردك...",
    replyLabel: "الرد",
    reply: "رد",
    setInProgress: "جاري",
    close: "إغلاق",
    low: "منخفض",
    normal: "عادي",
    high: "عالي",
    urgent: "هام",
    critical: "خطير",
    isInternal: "هل داخلي؟",
    internal: "ملاحظة داخلية",
    approved: "معتمد",
    disapproved: "غير معتمد",
    setPriority: "تغيير الأولوية",
    noMessages: "لا توجد رسائل بعد",
  },
  en: {
    total: "Total Tickets",
    openCount: "Open",
    inProgressCount: "In Progress",
    allTickets: "All Tickets",
    search: "Search...",
    searchLabel: "Search tickets",
    empty: "No tickets found",
    loading: "Loading tickets...",
    loadFailed: "Tickets could not be loaded.",
    retry: "Try again",
    actionFailed: "The action could not be completed. Please try again.",
    notAllowed: "You are not allowed to do this on this restaurant's ticket.",
    bulkAssign: "Bulk assign",
    deleteTicket: "Delete ticket",
    deleteConfirm: "This ticket will be permanently deleted.",
    byType: "By Type",
    byStatus: "By Status",
    byPriority: "By Priority",
    all: "All",
    open: "Open",
    assigned: "Assigned",
    in_progress: "In Progress",
    resolved: "Resolved",
    closed: "Closed",
    app_issue: "App Issue",
    menu_update: "Menu Update",
    working_hours: "Working Hours",
    invoice_request: "Invoice Request",
    rider_issue: "Rider Issue",
    app_status: "App Status",
    meeting_request: "Meeting Request",
    offer_request: "Campaign Request",
    registration_request: "New App Registration",
    update_data: "Update Data",
    other: "Other Inquiry",
    details: "Ticket Details",
    category: "Request Type",
    priority: "Priority",
    app: "App",
    orderNumber: "Order Number",
    description: "Description",
    timeline: "Timeline",
    messages: "Messages",
    replyPlaceholder: "Write your reply...",
    replyLabel: "Reply",
    reply: "Reply",
    setInProgress: "In Progress",
    close: "Close",
    low: "Low",
    normal: "Normal",
    high: "High",
    urgent: "Urgent",
    critical: "Critical",
    isInternal: "Is Internal?",
    internal: "Internal note",
    approved: "Approved",
    disapproved: "Not approved",
    setPriority: "Set priority",
    noMessages: "No messages yet",
  },
};

const TYPE_ICONS = {
  all: List,
  working_hours: Clock3,
  menu_update: FileText,
  app_issue: Smartphone,
  invoice_request: FileText,
  rider_issue: AlertCircle,
  app_status: ToggleRight,
  meeting_request: CalendarDays,
  offer_request: Megaphone,
  registration_request: Ticket,
  update_data: PauseCircle,
  other: MessageCircle,
};

const STATUS_ICONS = {
  open: Circle,
  assigned: MessageCircle,
  in_progress: Loader,
  resolved: Check,
  closed: XCircle,
  low: ArrowDown,
  normal: MoveHorizontal,
  high: ArrowUpFromDot,
  urgent: Flame,
  critical: ShieldAlert,
};

const TYPE_KEYS = [
  "all",
  "working_hours",
  "menu_update",
  "app_issue",
  "invoice_request",
  "rider_issue",
  "app_status",
  "meeting_request",
  "offer_request",
  "registration_request",
  "update_data",
  "other",
];

const STATUS_KEYS = ["open", "in_progress", "resolved", "closed"];
const PRIORITY_KEYS = ["critical", "urgent", "high", "normal", "low"];

const SupportTickets = () => {
  const {
    lang,
  } = useContext(Context);
  const { can, has } = useAccess();
  const t = TEXT[lang] || TEXT.ar;
  const isStoreSide = can("support.ticket.clientSide");
  const filtersHidden = can("support.ticket.filters.hidden");
  const statusPriorityOnly = can("support.ticket.filters.statusPriorityOnly");
  // display only (role); every action below checks the ticket's own restaurant
  const canManage = has("support.ticket.manage");
  // The ticket's restaurant is the public id the server populated on it. A ticket without
  // one cannot be verified and is refused (global roles pass by design).
  const allowedOn = (permission, ticket) =>
    can(permission, { restaurantId: ticket?.storeId?.id });

  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Reply drafts per ticket, so switching tickets neither loses a draft nor
  // sends it to the wrong ticket.
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  // One ticket action at a time; failures are shown, never swallowed.
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const detailsRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const pendingRef = useRef(false);

  const selectedId = selectedTicket?._id;
  const reply = (selectedId && drafts[selectedId]) || "";
  const setReply = (value) => {
    if (!selectedId) return;
    setDrafts((prev) => ({ ...prev, [selectedId]: value }));
  };

  const categoryByCode = useMemo(
    () => new Map(categories.map((c) => [c.code, c])),
    [categories],
  );

  const getCustomFields = useCallback(
    (ticket) => {
      const category = categoryByCode.get(ticket?.category);
      return Object.entries(ticket?.customFields || {}).map(([key, value]) => {
        const field = category?.formFields?.find((f) => f.key === key);
        return {
          key,
          label: field?.label?.[lang] || key,
          value,
        };
      });
    },
    [categoryByCode, lang],
  );

  const customFields = selectedTicket ? getCustomFields(selectedTicket) : [];

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) return;
    getCategoriesAPI(token)
      .then((res) => setCategories(normalizeCategories(res)))
      .catch((error) => console.error(error));
  }, []);

  const fetchTickets = useCallback(async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return;

    const requestId = ++listRequestRef.current;
    setLoading(true);
    try {
      const res = await getMySupportsAPI(token);
      if (requestId !== listRequestRef.current) return;
      setTickets(normalizeTickets(res));
      setLoadFailed(false);
    } catch (error) {
      console.error(error);
      if (requestId === listRequestRef.current) setLoadFailed(true);
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Loads a ticket's full details. Only the latest request may write state,
  // so clicking quickly between tickets can't show the wrong one.
  const loadTicketDetails = useCallback(async (ticket) => {
    const token = localStorage.getItem("auth-token");
    if (!token || !ticket?._id) return;

    const requestId = ++detailsRequestRef.current;
    setDetailsLoading(true);
    try {
      const res = await getSupportByIdAPI(token, ticket._id);
      if (requestId !== detailsRequestRef.current) return;
      setSelectedTicket(res?.data || res || ticket);
    } catch (error) {
      console.error(error);
      if (requestId === detailsRequestRef.current) setActionError(t.actionFailed);
    } finally {
      if (requestId === detailsRequestRef.current) setDetailsLoading(false);
    }
  }, [t.actionFailed]);

  const openTicket = (ticket) => {
    if (ticket?._id !== selectedId) {
      // The internal-note flag must not carry over to another ticket.
      setIsInternalNote(false);
      setActionError("");
    }
    setSelectedTicket(ticket);
    loadTicketDetails(ticket);
  };

  const filteredTickets = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const isStatus = STATUS_KEYS.includes(activeFilter);
    const isPriority = PRIORITY_KEYS.includes(activeFilter);

    return tickets.filter((ticket) => {
      const matchFilter =
        activeFilter === "all" ||
        (isStatus
          ? ticket?.status === activeFilter
          : isPriority
            ? ticket?.priority === activeFilter
            : ticket?.category === activeFilter);
      if (!matchFilter) return false;
      if (!keyword) return true;

      const customValues = Object.values(ticket?.customFields || {})
        .join(" ")
        .toLowerCase();

      return (
        ticket?.title?.toLowerCase()?.includes(keyword) ||
        ticket?.ticketNumber?.toLowerCase()?.includes(keyword) ||
        ticket?.category?.toLowerCase()?.includes(keyword) ||
        ticket?.description?.toLowerCase()?.includes(keyword) ||
        customValues.includes(keyword)
      );
    });
  }, [tickets, activeFilter, search]);

  // One pass over the tickets instead of one per sidebar entry.
  const counts = useMemo(() => {
    const result = { all: tickets.length };
    tickets.forEach((ticket) => {
      [ticket?.status, ticket?.priority, ticket?.category].forEach((key) => {
        if (key) result[key] = (result[key] || 0) + 1;
      });
    });
    return result;
  }, [tickets]);

  const sections = [
    !statusPriorityOnly && {
      title: t.byType,
      items: TYPE_KEYS.map((key) => ({
        key,
        label: t?.[key],
        icon: TYPE_ICONS[key],
        count: key === "all" ? counts.all : undefined,
      })),
    },
    {
      title: t.byStatus,
      items: [
        // "All" normally lives under By Type; without it a filter could not be cleared.
        ...(statusPriorityOnly
          ? [{ key: "all", label: t.all, icon: TYPE_ICONS.all, count: counts.all }]
          : []),
        ...STATUS_KEYS.map((key) => ({
          key,
          label: t[key],
          icon: STATUS_ICONS[key],
          count: counts[key] || 0,
          badgeClassName: key,
        })),
      ],
    },
    (!isStoreSide || statusPriorityOnly) && {
      title: t.byPriority,
      items: PRIORITY_KEYS.map((key) => ({
        key,
        label: t[key],
        icon: STATUS_ICONS[key],
        count: counts[key] || 0,
        badgeClassName: key,
      })),
    },
  ].filter(Boolean);

  // Runs one ticket action, then refreshes the list and the open ticket so
  // neither shows stale status/priority/messages.
  const runTicketAction = async (name, request, { refreshList = true } = {}) => {
    const ticket = selectedTicket;
    // handler-level authorization, for this ticket's restaurant: replies are open to the
    // client side, everything else needs ticket management
    const permission = name === "reply" ? "support.ticket.reply" : "support.ticket.manage";
    if (!ticket?._id || !allowedOn(permission, ticket)) {
      if (ticket?._id) setActionError(t.notAllowed);
      return false;
    }
    if (pendingRef.current) return false;
    const token = localStorage.getItem("auth-token");
    if (!token) return false;

    pendingRef.current = true;
    setPendingAction(name);
    setActionError("");
    try {
      await request(token, ticket._id);
      await Promise.all([
        refreshList ? fetchTickets() : null,
        loadTicketDetails(ticket),
      ]);
      return true;
    } catch (error) {
      console.error(error);
      setActionError(error?.message || t.actionFailed);
      return false;
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  };

  const handleReply = async () => {
    const text = reply;
    if (!text.trim()) return;
    const ticketId = selectedId;
    const internal = isInternalNote;
    if (internal && !allowedOn("support.ticket.manage", selectedTicket)) return;
    const ok = await runTicketAction(
      "reply",
      (token, id) =>
        internal ? addInternalNoteAPI(token, id, text) : addSupportMessageAPI(token, id, text),
      { refreshList: false },
    );
    if (ok) {
      // Only clear the draft once the message is actually saved.
      setDrafts((prev) => ({ ...prev, [ticketId]: "" }));
      setIsInternalNote(false);
    }
  };

  const handleSetInProgress = () =>
    runTicketAction("in_progress", (token, id) =>
      updateSupportStatusAPI(token, id, "in_progress"),
    );

  const handleSetResolved = () =>
    runTicketAction("resolved", (token, id) =>
      updateSupportStatusAPI(token, id, "resolved"),
    );

  const handleClose = () =>
    runTicketAction("close", (token, id) => closeSupportAPI(token, id));

  const handlePriority = (priority) =>
    runTicketAction(`priority:${priority}`, (token, id) =>
      updateSupportPriorityAPI(token, id, priority),
    );

  // Returned to ConfirmModal, which stays busy until this settles.
  const handleDelete = async () => {
    const ticket = deleteTarget;
    if (!allowedOn("support.ticket.delete", ticket)) {
      setDeleteTarget(null);
      setActionError(t.notAllowed);
      return;
    }
    const token = localStorage.getItem("auth-token");
    if (!ticket?._id || !token) return;
    setActionError("");
    try {
      await deleteSupportAPI(token, ticket._id);
      if (selectedId === ticket._id) setSelectedTicket(null);
      setSelectedIds((prev) => prev.filter((id) => id !== ticket._id));
      setDeleteTarget(null);
      // The list used to keep showing the deleted ticket.
      await fetchTickets();
    } catch (error) {
      console.error(error);
      setDeleteTarget(null);
      setActionError(error?.message || t.actionFailed);
    }
  };

  return (
    <div className="support-tickets-page">
      <div className={`support-tickets-layout ${filtersHidden ? "no-filters" : ""}`}>
        {!filtersHidden && (
          <FilterSidebar
            sections={sections}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
        )}

        <main className="support-tickets-main">
          {canManage && bulkMode && (
            <BulkAssignBar
              selectedIds={selectedIds}
              tickets={tickets}
              onDone={() => {
                setBulkMode(false);
                setSelectedIds([]);
                fetchTickets();
              }}
            />
          )}
          <div className="support-tickets-header">
            <h2>{t.allTickets}</h2>

            <div className="support-tickets-tools">
              {canManage && (
                <Button
                  variant="primary"
                  appearance="soft"
                  rounded
                  icon={<UserPen size={16} />}
                  aria-label={t.bulkAssign}
                  title={t.bulkAssign}
                  aria-pressed={bulkMode}
                  onClick={() => setBulkMode((prev) => !prev)}
                />
              )}
              <div className="support-tickets-search">
                <Input
                  type="search"
                  value={search}
                  placeholder={t.search}
                  aria-label={t.searchLabel}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {actionError && !selectedTicket && (
            <p className="support-action-error" role="alert">
              {actionError}
            </p>
          )}

          {loading && !tickets.length ? (
            <div className="support-ticket-empty" role="status">
              {t.loading}
            </div>
          ) : loadFailed && !tickets.length ? (
            <div className="support-ticket-empty" role="alert">
              {t.loadFailed}{" "}
              <Button size="sm" appearance="soft" onClick={fetchTickets}>
                {t.retry}
              </Button>
            </div>
          ) : !filteredTickets.length ? (
            <div className="support-ticket-empty">{t.empty}</div>
          ) : (
            <TicketList
              tickets={filteredTickets}
              selectedTicket={selectedTicket}
              bulkMode={bulkMode}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              openTicket={openTicket}
              onDelete={(ticket) => setDeleteTarget(ticket)}
              getCustomFields={getCustomFields}
              t={t}
            />
          )}

          {selectedTicket && (
            <TicketDetails
              key={selectedTicket._id}
              selectedTicket={selectedTicket}
              detailsLoading={detailsLoading}
              customFields={customFields}
              t={t}
              reply={reply}
              setReply={setReply}
              isInternalNote={isInternalNote}
              setIsInternalNote={setIsInternalNote}
              pendingAction={pendingAction}
              actionError={actionError}
              handleReply={handleReply}
              handleClose={handleClose}
              handleSetResolved={handleSetResolved}
              handleSetInProgress={handleSetInProgress}
              handlePriority={handlePriority}
            />
          )}
        </main>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title={t.deleteTicket}
          message={`${deleteTarget.ticketNumber || deleteTarget.title || ""} — ${t.deleteConfirm}`}
          tone="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default SupportTickets;
